import React, { useMemo, useState, useEffect } from "react";
import CardBots from "./CardBots";
import { ChatBot } from "../../services/dashboard/GetChatBots";
import {
  useReactTable,
  getCoreRowModel,
  getPaginationRowModel,
  getFilteredRowModel,
  ColumnDef,
  CellContext,
  PaginationState
} from '@tanstack/react-table';
import "../../styles/dash/BotsSection.css";
import { useNavigate } from "react-router-dom";

// Interfaces para los permisos
interface Permission {
    Id: number;
    IdAction: number;
    Action: string;
    IdModule: number;
    ModuleName: string;
}

interface Role {
    Id: number;
    RoleName: string;
    Permissions: Permission[];
}

interface BotsSectionProps {
  bots: ChatBot[];
  permisos: number[];
  toggleBotStatus: (id: number) => void;
  setBotSeleccionado: (bot: ChatBot) => void;
  setMostrarFormulario: (value: boolean) => void;
  refreshBots: () => void;
  mostrarTabla: boolean;
  setMostrarTabla: (value: boolean) => void;
  currentCardPage: number;
  setCurrentCardPage: (page: number | ((prev: number) => number)) => void;
  globalFilter: string;
  setGlobalFilter: (filter: string) => void;
}

const BotsSection: React.FC<BotsSectionProps> = ({
  bots,
  permisos,
  toggleBotStatus,
  setBotSeleccionado,
  setMostrarFormulario,
  mostrarTabla,
  setMostrarTabla,
  currentCardPage,
  setCurrentCardPage,
  globalFilter,
  setGlobalFilter
}) => {
  const navigate = useNavigate();

  useEffect(() => {
    let hasPermission = false;
    const userData = localStorage.getItem("user");
    if (userData) {
        try {
            const userObject = JSON.parse(userData);
            const userDataParsed = JSON.parse(userObject.userData || "{}");
            
            if (userDataParsed?.roles?.length) {
                const permisosAplanados = userDataParsed.roles.flatMap((rol: Role) =>
                    rol.Permissions.map((p: Permission) => p.IdAction)
                );
                if (permisosAplanados.includes(7)) {
                    hasPermission = true;
                }
            }
        } catch (err) {
            console.error('Error parsing user data:', err);
        }
    }
    if (!hasPermission) {
        navigate("/login");
    }
}, [navigate]);

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });

  const columns = useMemo<ColumnDef<ChatBot>[]>(() => [
    {
      id: 'actions',
      header: 'Acciones',
      cell: (info: CellContext<ChatBot, unknown>) => {
        const bot = info.row.original;
        const powerButtonColorClass = bot.status === 1 ? 'power-off' : 'power-on';
        const powerButtonText = bot.status === 1 ? 'Desactivar' : 'Activar';

        return (
          <div className="table-actions">
            <button
              disabled={!permisos.includes(10)}
              className={`action-button power ${powerButtonColorClass}`}
              title={powerButtonText}
              onClick={() => toggleBotStatus(bot.id)}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="12" cy="12" r="9" />
                <line x1="12" y1="4" x2="12" y2="8" />
              </svg>
            </button>

            <button
              disabled={!permisos.includes(11)}
              className="action-button edit"
              title="Editar"
              onClick={() => {
                setBotSeleccionado(bot);
                setMostrarFormulario(true);
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                fill="white"
                viewBox="0 0 24 24"
              >
                <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25z" />
                <path d="M20.71 7.04a1.003 1.003 0 0 0 0-1.41l-2.34-2.34a1.003 1.003 0 0 0-1.41 0L15.13 4.9l3.75 3.75 1.83-1.61z" />
              </svg>
            </button>
          </div>
        );
      },
    },
    {
      accessorKey: 'nombre',
      header: 'Nombre',
    },
    {
      accessorKey: 'key',
      header: 'Key',
      cell: (info: CellContext<ChatBot, unknown>) => (
        <span className="">{info.getValue() as string}</span>
      ),
    },
    {
      accessorKey: 'idCliente',
      header: 'Cliente',
      cell: (info: CellContext<ChatBot, unknown>) => (info.getValue() as number) || 'N/A',
    },
    {
      accessorKey: 'nombreAgente',
      header: 'Agente',
      cell: (info: CellContext<ChatBot, unknown>) => (info.getValue() as string) || 'N/A',
    },
    {
      accessorKey: 'color',
      header: 'Color',
      cell: (info: CellContext<ChatBot, unknown>) => (
        <span className="color-badge" style={{ backgroundColor: (info.getValue() as string) || '#ccc' }}>
          {info.getValue() as string}
        </span>
      ),
    },
    {
      accessorKey: 'status',
      header: 'Estado',
      cell: (info: CellContext<ChatBot, unknown>) => {
        const status = info.getValue() as number;
        const statusText = status === 1 ? 'Activo' : 'Inactivo';
        return (
          <span className={`status-text ${statusText.toLowerCase()}`}>
            {statusText}
          </span>
        );
      },
    },
  ], [permisos, toggleBotStatus, setBotSeleccionado, setMostrarFormulario]);

  const table = useReactTable({
    data: bots,
    columns,
    state: {
      globalFilter,
      pagination,
    },
    onGlobalFilterChange: setGlobalFilter,
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  function getPaginationRange(current: number, total: number): (number | string)[] {
    const delta = 1;
    const range: (number | string)[] = [];
    const rangeWithDots: (number | string)[] = [];
    let l = 0;

    for (let i = 1; i <= total; i++) {
      if (i === 1 || i === total || (i >= current - delta && i <= current + delta)) {
        range.push(i);
      }
    }

    for (let i of range) {
      if (l > 0) {
        if (Number(i) - l === 2) {
          rangeWithDots.push(l + 1);
        } else if (Number(i) - l !== 1) {
          rangeWithDots.push("...");
        }
      }
      rangeWithDots.push(i);
      l = Number(i);
    }

    return rangeWithDots;
  }

  const cardsPerPage = 4;
  const totalCardPages = Math.ceil(bots.length / cardsPerPage);
  const startCardIndex = (currentCardPage - 1) * cardsPerPage;
  const currentCardBots = bots.slice(startCardIndex, startCardIndex + cardsPerPage);

  return (
    <div className="dashboard-container">

      <div className="dashboard-toolbar">
        <label className="toggle-switch">
          <input
            type="checkbox"
            checked={mostrarTabla}
            onChange={() => {
              setMostrarTabla(!mostrarTabla);
            }}
          />
          <span className="slider round"></span>
          <span className="label-text">Mostrar tabla</span>
        </label>
      </div>

      {/* Barra de búsqueda solo para vista de tabla */}
      <div className="dashboard-header">
        <div className="roles-header-toolbar">
          <button
            disabled={!permisos.includes(8)}
            className="add-bot-tab-button"
            onClick={() => {
              setBotSeleccionado(null as any);
              setMostrarFormulario(true);
            }}
          >
            Añadir nuevo ChatBot
          </button>

          {mostrarTabla && (
            <div className="search-container">
              <input
                type="text"
                placeholder="Buscar chatbot..."
                value={globalFilter}
                onChange={(e) => setGlobalFilter(e.target.value)}
                className="global-filter"
              />
            </div>
          )}
        </div>
      </div>

      <div className="demo-request-table-container">
        {mostrarTabla ? (
          <>
            <div className="chatbots-table-container">
              <table className="chatbots-table">
                <colgroup>
                  <col style={{ width: '12%' }} /><col style={{ width: '15%' }} /><col style={{ width: '20%' }} /><col style={{ width: '13%' }} /><col style={{ width: '13%' }} /><col style={{ width: '12%' }} /><col style={{ width: '15%' }} />
                </colgroup>
                <thead>
                  <tr>
                    <th className="actions-column">Acciones</th>
                    <th className="nombre-column">Nombre</th>
                    <th>Key</th>
                    <th className="client-column">Cliente</th>
                    <th className="client-column">Agente</th>
                    <th className="color-column">Color</th>
                    <th className="state-column">Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {table.getRowModel().rows.map(row => {
                    const bot = row.original;
                    const statusText = bot.status === 1 ? 'Activo' : 'Inactivo';
                    const powerButtonText = bot.status === 1 ? 'Desactivar' : 'Activar';
                    const powerButtonColorClass = bot.status === 1 ? 'power-off' : 'power-on';

                    return (
                      <tr key={row.id}>
                        <td className="actions-column">
                          <div className="table-actions">
                            <button
                              disabled={!permisos.includes(10)}
                              className={`action-button power ${powerButtonColorClass}`}
                              title={powerButtonText}
                              onClick={() => toggleBotStatus(bot.id)}
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="16"
                                height="16"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="3"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <circle cx="12" cy="12" r="9" />
                                <line x1="12" y1="4" x2="12" y2="8" />
                              </svg>
                            </button>

                            <button
                              disabled={!permisos.includes(11)}
                              className="action-button edit"
                              title="Editar"
                              onClick={() => {
                                setBotSeleccionado(bot);
                                setMostrarFormulario(true);
                              }}
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="16"
                                height="16"
                                fill="white"
                                viewBox="0 0 24 24"
                              >
                                <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25z" />
                                <path d="M20.71 7.04a1.003 1.003 0 0 0 0-1.41l-2.34-2.34a1.003 1.003 0 0 0-1.41 0L15.13 4.9l3.75 3.75 1.83-1.61z" />
                              </svg>
                            </button>
                          </div>
                        </td>
                        <td className="nombre-column">{bot.nombre}</td>
                        <td className="">{bot.key}</td>
                        <td className="client-column">{bot.idCliente || 'N/A'}</td>
                        <td className="client-column">{bot.nombreAgente || 'N/A'}</td>
                        <td className="color-column">
                          <span className="color-badge" style={{ backgroundColor: bot.color || '#ccc' }}>
                            {bot.color}
                          </span>
                        </td>
                        <td className="state-column">
                          <span className={`status-text ${statusText.toLowerCase()}`}>
                            {statusText}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Paginación avanzada para tabla */}
            <div className="pagination-controls">
              <button
                onClick={() => table.previousPage()}
                disabled={!table.getCanPreviousPage()}
              >
                ←
              </button>

              {getPaginationRange(
                table.getState().pagination.pageIndex + 1,
                table.getPageCount()
              ).map((page, idx) =>
                page === "..." ? (
                  <span key={idx} className="dots">...</span>
                ) : (
                  <button
                    key={idx}
                    className={
                      table.getState().pagination.pageIndex + 1 === page
                        ? "active-page"
                        : ""
                    }
                    onClick={() => table.setPageIndex(Number(page) - 1)}
                  >
                    {page}
                  </button>
                )
              )}

              <button
                onClick={() => table.nextPage()}
                disabled={!table.getCanNextPage()}
              >
                →
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="bots-carousel">
              {currentCardBots.length > 0 ? (
                currentCardBots.map((bot) => (
                  <CardBots
                    key={bot.id}
                    bot={bot}
                    permisos={permisos}
                    onToggleStatus={toggleBotStatus}
                    onEdit={() => {
                      setBotSeleccionado(bot);
                      setMostrarFormulario(true);
                    }}
                  />
                ))
              ) : (
                <div className="no-bots-message">
                  <p>No hay chatbots disponibles</p>
                </div>
              )}
            </div>

            {/* Paginación independiente para cards */}
            <div className="pagination-controls">
              <button
                disabled={currentCardPage === 1}
                onClick={() => setCurrentCardPage(prev => Math.max(1, prev - 1))}
                className="btn-next-dashboard1"
              >
                Anterior
              </button>

              {Array.from({ length: totalCardPages }, (_, i) => i + 1).map(page => (
                <button
                  key={page}
                  className={currentCardPage === page ? "active-page" : ""}
                  onClick={() => setCurrentCardPage(page)}
                >
                  {page}
                </button>
              ))}

              <button
                disabled={currentCardPage >= totalCardPages || totalCardPages === 0}
                onClick={() => setCurrentCardPage(prev => prev + 1)}
                className="btn-next-dashboard2"
              >
                Siguiente
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default BotsSection;